import type { TApplicantDetail } from '@/services/applicants';

type TMemberRecordTabID = 'profile' | 'preferences' | 'review' | 'activity' | 'billing';

type TMemberRecordTab = {
  value: TMemberRecordTabID;
  label: string;
};

type TMemberFieldSpec = {
  key: keyof TApplicantDetail;
  label: string;
  isHeight?: boolean;
  isAge?: boolean;
};

type TActivityViewID = 'messages' | 'matches' | 'likes';

type TActivityView = {
  value: TActivityViewID;
  label: string;
};

type TActivityColumnID = 'client' | 'pair' | 'single' | 'action' | 'date' | 'state';

type TActivityColumnSpec = {
  key: TActivityColumnID;
  label: string;
  width: string;
};

type TActivityRow = {
  id: string;
  clientName: string;
  singleName: string;
  action: string;
  date: string;
  state: string;
};

type TReviewNoteKind = 'photo' | 'photos' | 'bio' | 'prompt';

type TReviewNoteTarget = {
  kind: TReviewNoteKind;
  id: string;
};

const MEMBER_RECORD_TAB_PARAM = 'tab';

const MEMBER_RECORD_TABS: readonly TMemberRecordTab[] = [
  { value: 'profile', label: 'Profile Info' },
  { value: 'preferences', label: 'Preferences' },
  { value: 'review', label: 'Need Review' },
  { value: 'activity', label: 'Messages · Matches' },
  { value: 'billing', label: 'Billing & Settings' },
];

const MEMBER_RECORD_COPY = {
  memberSince: 'Member since',
  book: 'Book:',
  message: 'Message',
  createRec: 'Create Rec',
  blank: '—',
  errorDescription: 'Reload the page or go back to the list and open the record again.',
  notFoundTitle: 'Member not found.',
  notFoundDescription: 'The record does not exist, or it is outside what your role can read.',
  back: 'Back to the list',
  billingTitle: 'Billing & Settings',
  billingDescription: 'This tab arrives with the billing stage.',
  visible: 'Visible to others',
  hidden: 'Not visible to others',
  openAccess: 'Open full access',
  accessOpen: 'Access open',
  accessOpened: 'Full access opened',
} as const;

const CONCIERGE_SESSION_COMPLETE_ACTION = 'concierge_session_complete';

const INTRO_CALL_COPY = { title: 'Intro Call' } as const;

const CONCIERGE_ACCESS_ERROR_COPY: Record<string, string> = {
  '42501': 'Your role cannot open access.',
  'no_session': 'No concierge session is booked for this member.',
};

const MEMBER_PROFILE_COPY = {
  personalTitle: 'Personal information',
  joinedTitle: 'How they joined',
  joinedEmpty: 'No invitation on record — this member did not arrive on a link.',
  bioTitle: 'Bio',
  bioLive: 'Live version',
  bioEmpty: 'No bio on file yet.',
  bioFootnote: 'Edits route to Need Review — manual concierge approval (MVP). The live profile stays exactly as-is until approved.',
  promptsTitle: 'Prompts',
  promptsEmpty: 'No live prompts yet.',
  promptsFootnote: 'Written in the app. The member picks which approved answers show, and in what order — up to three.',
  photosTitle: 'Photos',
  photosLive: 'live',
  photosInReview: 'in review',
  primary: 'Primary',
  photoOpen: 'Open photo',
  photoClose: 'Close',
  scoringTitle: 'Internal scoring',
  scoringPill: 'Internal — never member-facing',
  qualityScore: 'Quality score',
  archetype: 'Archetype',
  selectivityTier: 'Selectivity tier',
  scoringFootnote: 'Set at interview — read-only here. Both sides are scored internally.',
  adminTitle: 'Admin actions',
  adminSubtitle: 'Guarded overrides — every one confirmed, attributed, logged.',
  changeType: 'Change user type…',
  transfer: 'Transfer to another concierge',
  transferNote: 'Requires members.reassign — Concierge Manager or Super Admin.',
} as const;

const MEMBER_ADMIN_COPY = {
  changeTypeTitle: 'Change user type',
  changeTypeDescription: 'Corrects a persona set wrong at activation. A client with billing on file cannot be changed.',
  changeTypeConfirm: 'Change type',
  transferTitle: 'Transfer to another concierge',
  transferDescription: 'Moves this member to another book permanently. The reassignment is logged.',
  transferPlaceholder: 'Choose a concierge',
  transferConfirm: 'Transfer',
  changeTo: 'Change to',
  roleClient: 'Client',
  roleSingle: 'Single',
  cancel: 'Cancel',
} as const;

const MEMBER_FIELDS: readonly TMemberFieldSpec[] = [
  { key: 'firstName', label: 'Full name' },
  { key: 'birthDate', label: 'Age', isAge: true },
  { key: 'city', label: 'City' },
  { key: 'occupation', label: 'Occupation' },
  { key: 'height', label: 'Height', isHeight: true },
  { key: 'children', label: 'Children' },
  { key: 'instagramURL', label: 'Instagram' },
  { key: 'linkedinURL', label: 'LinkedIn' },
];

const MEMBER_PREFERENCES_COPY = {
  ranksTitle: 'Ranked priorities',
  ranksSubtitle: 'Top three, in their order — the deck leans on them, softly.',
  ranksEmpty: 'No ranking yet — the member has not ordered their preferences.',
  binariesTitle: 'The three binaries',
  intent: 'Intent',
  familyPlans: 'Family plans',
  existingChildren: 'Existing children',
  softTitle: 'Soft preferences',
  ageRange: 'Age range',
  distance: 'Distance',
  height: 'Height',
  culturalBackground: 'Cultural background',
  culturalValue: 'Soft — influences, never excludes',
  selectivity: 'Selectivity',
  selectivityNote: 'Tuning input for drops — staff-only, never member-facing.',
  notesTitle: 'Concierge notes',
  notesPlaceholder: 'Add a note for the team…',
  notesPrivacy: 'Staff-only — never visible to',
  addNote: 'Add note',
  notesEmpty: 'No notes yet.',
  changesTitle: 'Preference changes',
  autoApproved: 'Auto-approved',
  changesFootnote: 'Preference edits apply instantly — nothing here waits on you.',
  changesEmpty: 'No preference changes yet.',
  distanceUnit: 'mi',
  distancePrefix: '≤',
  heightSuffix: 'and above',
  heightPrefix: 'up to',
  set: 'Set',
  widened: 'Widened',
  narrowed: 'Narrowed',
  changed: 'Changed',
  updated: 'Updated',
} as const;

const PREFERENCE_TOP_RANKS = 3;

const PREFERENCE_CRITERION_LABEL: Record<string, string> = {
  'age': 'Age',
  'height': 'Height',
  'distance': 'Distance',
  'dating_objectives': 'Intent',
  'relationship_status': 'Relationship status',
  'education_level': 'Education',
  'family_goals': 'Family plans',
  'children': 'Existing children',
  'ethnicity': 'Cultural background',
  'religion': 'Religion',
  'financial_role': 'Financial role',
};

const PREFERENCE_CHANGE_LABEL: Record<string, string> = {
  'age': 'age range',
  'height': 'height',
  'distance_max': 'distance',
  'dating_objectives': 'intent',
  'family_goals': 'family plans',
  'children': 'existing children',
  'ethnicity': 'cultural background',
  'religion': 'religion',
  'education_level': 'education',
  'relationship_status': 'relationship status',
  'global_firmness': 'firmness',
};

const MEMBER_REVIEW_COPY = {
  banner: 'Their live profile stays exactly as-is until you approve — members never see an in-review state.',
  emptyTitle: 'Nothing awaiting review',
  emptyDescription: 'Photo, bio and work edits appear here for approval.',
  photosTitle: 'New photos',
  submitted: 'Submitted',
  criteriaTitle: 'Photo standards — review criteria',
  approveAll: 'Approve all',
  approveBoth: 'Approve both',
  returnAll: 'Return all',
  returnBoth: 'Return both',
  bioTitle: 'Bio edit',
  bioChip: 'Awaiting approval',
  bioNote: 'Every bio and profile edit routes here for manual concierge approval — MVP. Nothing auto-publishes.',
  current: 'Current — live',
  proposed: 'Proposed',
  currentEmpty: 'No live bio yet.',
  approve: 'Approve',
  returnWithNote: 'Return with note',
  promptTitle: 'Prompt edit',
  workTitle: 'Work edit',
  workChip: 'Awaiting approval',
  workNote: 'A member asks to change their work — approving writes it to their profile, declining leaves it as it is.',
  workCurrentEmpty: 'No work on the live profile yet.',
  workDecline: 'Decline',
  noteTitle: 'Return with a note',
  notePhotosDescription: 'One note covers the whole set — the member reads it.',
  noteDescription: 'The member reads this note.',
  notePlaceholder: 'What should the member change?',
  noteConfirm: 'Return',
  cancel: 'Cancel',
} as const;

const MEMBER_WORK_ERROR_COPY: Record<string, string> = {
  '22023': 'The decision was not accepted. Reopen the record and try again.',
  'P0002': 'This request is gone — someone may have reviewed it already. Refresh the queue.',
};

const PHOTO_CRITERIA: readonly string[] = [
  '5–6 photos in the live set — these replace',
  '≥ 1 clear face',
  '≥ 1 full body',
  'First photo — no sunglasses',
  'First photo — not a group shot',
];

const ACTIVITY_VIEWS: readonly TActivityView[] = [
  { value: 'messages', label: 'Messages' },
  { value: 'matches', label: 'Matches' },
  { value: 'likes', label: 'Likes & Passes' },
];

const MEMBER_ACTIVITY_COPY = {
  replyPrefix: 'Reply to',
  pairSeparator: '×',
  threadEmpty: 'No messages in this thread yet.',
  threadMissing: 'There is no thread with this member yet.',
  threadForeign: 'This thread is between the member and their concierge',
  threadMonitor: 'Read-only — you are viewing another concierge\'s thread.',
  chatOffline: 'Chat is not connected in this environment.',
  matchesEmpty: 'No matches yet.',
  likesEmpty: 'No likes or passes yet.',
  matched: 'Matched',
  matchActive: 'Active',
  matchUnmatched: 'Unmatched',
  passByClient: 'Passed by client',
  passBySingle: 'Passed by single',
  undone: 'Undone',
  errorDescription: 'Nothing was loaded. Reload the page or try again in a moment.',
} as const;

const APPROACH_KIND_LABEL: Record<string, string> = {
  'like': 'Liked',
  'warm_intro': 'Warm intro',
  'second_look': 'Second look',
  'follow_up': 'Follow-up',
};

const ACTIVITY_COLUMNS: readonly TActivityColumnSpec[] = [
  { key: 'client', label: 'Client', width: 'w-[250px]' },
  { key: 'pair', label: '', width: 'w-[36px]' },
  { key: 'single', label: 'Single', width: 'w-[250px]' },
  { key: 'action', label: 'Action', width: 'w-[170px]' },
  { key: 'date', label: 'Date', width: 'w-[130px]' },
  { key: 'state', label: '', width: 'w-auto' },
];

export type {
  TActivityColumnID,
  TActivityRow,
  TActivityColumnSpec,
  TActivityView,
  TActivityViewID,
  TMemberFieldSpec,
  TMemberRecordTab,
  TMemberRecordTabID,
  TReviewNoteKind,
  TReviewNoteTarget,
};
export {
  ACTIVITY_COLUMNS,
  ACTIVITY_VIEWS,
  APPROACH_KIND_LABEL,
  CONCIERGE_ACCESS_ERROR_COPY,
  CONCIERGE_SESSION_COMPLETE_ACTION,
  INTRO_CALL_COPY,
  MEMBER_ACTIVITY_COPY,
  MEMBER_ADMIN_COPY,
  MEMBER_FIELDS,
  MEMBER_PREFERENCES_COPY,
  MEMBER_PROFILE_COPY,
  MEMBER_RECORD_COPY,
  MEMBER_RECORD_TAB_PARAM,
  MEMBER_RECORD_TABS,
  MEMBER_REVIEW_COPY,
  MEMBER_WORK_ERROR_COPY,
  PHOTO_CRITERIA,
  PREFERENCE_CHANGE_LABEL,
  PREFERENCE_CRITERION_LABEL,
  PREFERENCE_TOP_RANKS,
};
